import { useCallback, useEffect, useMemo } from 'react';

import { AdyenPlatformExperience, TransactionsOverview } from '@adyen/adyen-platform-experience-web';
import { Box, Card } from '@mui/material';
import { useSnackbar } from 'notistack';

import LoadingOverlay from '../../../components/LoadingOverlay';
import useApp from '../../../hooks/useApp';
import { getErrorMessage } from '../../../lib/error';
import { getSDKClient } from '../../../lib/sdk';

type AdyenSessionToken = {
  id: string;
  token: string;
};

type AdyenTransactionOverviewComponentProps = {
  isComponentInitialized: boolean;
  setIsComponentInitialized: (isComponentInitialized: boolean) => void;
  customerAppId?: string;
  getAdyenSessionToken: () => Promise<AdyenSessionToken>;
  live: boolean;
  balanceAccountId?: string;
};

// Custom field keys for transaction data customization
enum CustomFieldKey {
  PAYER = 'Payer',
  FV_TRANSACTION_REFERENCE = 'FV Trx Reference',
}

export default function AdyenTransactionOverviewComponent({
  isComponentInitialized,
  setIsComponentInitialized,
  customerAppId,
  getAdyenSessionToken,
  live,
  balanceAccountId,
}: AdyenTransactionOverviewComponentProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { app } = useApp();

  const sdkClient = useMemo(() => {
    const { tokens, env } = app;
    const currentToken = tokens.get(env) ?? '';
    return getSDKClient(env, currentToken);
  }, [app]);

  const fetchPaymentsByAdyenPspReferences = useCallback(
    async (pspReferences: string[]) => {
      if (!customerAppId) {
        return [];
      }

      try {
        const { payments } = await sdkClient.getPaymentsByAdyenPspReferences(customerAppId, pspReferences);
        return payments;
      } catch (error) {
        enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
        return [];
      }
    },
    [customerAppId, sdkClient, enqueueSnackbar],
  );

  useEffect(() => {
    if (!customerAppId) {
      return;
    }

    let transactionsOverviewInstance: InstanceType<typeof TransactionsOverview> | null = null;

    const initializeAdyenTransactionsOverview = async () => {
      try {
        setIsComponentInitialized(false);

        const adyenPlatformCore = await AdyenPlatformExperience({
          onSessionCreate: getAdyenSessionToken,
          environment: live ? 'live' : 'test',
        });

        transactionsOverviewInstance = new TransactionsOverview({
          core: adyenPlatformCore,
          balanceAccountId, // scopes to the specific balance account if provided
          dataCustomization: {
            list: {
              // Define fields for the transaction list view
              fields: [
                // For amount and gross amount, their default alignment is right. To make everything consistent, we align them to left.
                {
                  key: 'amount',
                  align: 'left',
                },
                {
                  key: 'grossAmount',
                  align: 'left',
                },
                // Custom fields
                {
                  key: CustomFieldKey.PAYER,
                },
                {
                  key: CustomFieldKey.FV_TRANSACTION_REFERENCE,
                },
              ],
              // This maps the payment data to custom fields (e.g. payer name, FV transaction reference)
              onDataRetrieve: async (transactionData) => {
                const pspReferences = transactionData.reduce<string[]>((acc, item) => {
                  if (item?.paymentPspReference) {
                    acc.push(item.paymentPspReference);
                  }
                  return acc;
                }, []);

                if (pspReferences.length === 0) {
                  return transactionData;
                }

                const uniquePspReferences = Array.from(new Set(pspReferences));

                const payments = await fetchPaymentsByAdyenPspReferences(uniquePspReferences);

                return transactionData.map((transaction) => {
                  const relatedPayment = payments.find(
                    (payment) =>
                      payment.payment_details.processor_details?.processor_reference ===
                      transaction.paymentPspReference,
                  );

                  return {
                    ...transaction,
                    [CustomFieldKey.FV_TRANSACTION_REFERENCE]:
                      relatedPayment?.payment_details.references?.finverse_transaction_reference ?? '',
                    [CustomFieldKey.PAYER]: relatedPayment?.sender.name ?? '',
                  };
                });
              },
            },
          },
        });

        transactionsOverviewInstance.mount('#transactions-overview-container');
      } catch (error) {
        enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
      }
    };

    initializeAdyenTransactionsOverview();

    return () => {
      if (transactionsOverviewInstance) {
        transactionsOverviewInstance.unmount();
        transactionsOverviewInstance = null;
      }
    };
  }, [
    getAdyenSessionToken,
    live,
    balanceAccountId,
    customerAppId,
    setIsComponentInitialized,
    enqueueSnackbar,
    fetchPaymentsByAdyenPspReferences,
  ]);

  return (
    <Box id="transactions-overview-container" sx={{ position: 'relative' }}>
      {!isComponentInitialized && (
        <Card sx={{ minHeight: 300, position: 'relative' }}>
          <LoadingOverlay />
        </Card>
      )}
    </Box>
  );
}
